/* Theme-local fulfillment display. Does not modify inventory, carts or checkout. */
if (!customElements.get('sd-product-fulfillment')) {
  class SpeedyDroneProductFulfillment extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.scope = this.closest('.m-main-product');
      const dataNode = this.querySelector('[data-sd-fulfillment-data]');
      if (!this.scope || !dataNode) return;
      try {
        this.data = JSON.parse(dataNode.textContent);
      } catch (_error) {
        return; // Keep the truthful server-rendered state if data cannot be read.
      }

      this.currentVariantId = String(this.dataset.initialVariant);
      const fromInput = (event) => {
        if (!event.target.matches('input[name="id"], select[name="id"]')) return;
        if (!event.target.closest('product-form.m-product-form--main')) return;
        this.applyVariant(event.target.value);
      };
      const fromOptions = (event) => {
        const sticky = event.target.closest('sticky-atc');
        if (sticky) {
          this.updateSticky(sticky.querySelector('[name="id"]')?.value);
          return;
        }
        queueMicrotask(() => {
          if (!this.isConnected) return;
          const picker = this.scope.querySelector('variant-picker');
          // A missing option combination has no currentVariant. Do not keep a
          // previous variant's delivery promise while the buy button is disabled.
          if (picker && 'currentVariant' in picker) {
            this.applyVariant(picker.currentVariant?.id);
          } else {
            this.applyVariant(this.readFormVariant());
          }
        });
      };
      const fromVariant = (event) => {
        const variantId = event.detail?.variant?.id;
        if (!variantId || String(variantId) !== String(this.readFormVariant())) return;
        this.applyVariant(variantId);
      };
      const afterMetaUpdate = () => this.applyVariant(this.currentVariantId);

      this.scope.addEventListener('input', fromInput);
      this.scope.addEventListener('change', fromOptions);
      this.scope.addEventListener('product:meta-updated', afterMetaUpdate, true);
      document.addEventListener('variant:changed', fromVariant);
      // Theme editor sections may be replaced without reloading the page.
      const afterSectionLoad = () => this.applyVariant(this.currentVariantId);
      document.addEventListener('shopify:section:load', afterSectionLoad);
      this.cleanup = () => {
        this.scope.removeEventListener('input', fromInput);
        this.scope.removeEventListener('change', fromOptions);
        this.scope.removeEventListener('product:meta-updated', afterMetaUpdate, true);
        document.removeEventListener('variant:changed', fromVariant);
        document.removeEventListener('shopify:section:load', afterSectionLoad);
      };
      this.applyVariant(this.readFormVariant() || this.currentVariantId);
    }

    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }

    readFormVariant() {
      return this.scope.querySelector('product-form.m-product-form--main [name="id"]')?.value;
    }

    applyVariant(variantId) {
      const id = variantId == null ? '' : String(variantId);
      const state = this.data.variants[id] || 'unknown';
      this.currentVariantId = id;
      const message = this.data.messages[state];
      this.hidden = !message;
      const scopes = [this.scope, ...document.querySelectorAll(
        `[data-section-type="product-details-tabs"][data-sd-fulfillment-product="${this.data.productId}"]`
      )];
      scopes.forEach((scope) => {
        scope.dataset.sdFulfillmentState = state;
        scope.querySelectorAll('[data-sd-fulfillment-notice]').forEach((notice) => {
          notice.hidden = !message;
          if (!message) return;
          notice.querySelector('[data-sd-fulfillment-title]').textContent = message.title;
          notice.querySelector('[data-sd-fulfillment-body]').textContent = message.body;
        });
      });
      const isSupplierStock = state === 'supplier-in-stock' || state === 'supplier-in-stock-pickup';
      const isBackorder = state.includes('backorder') || state === 'supplier-pickup' || state === 'supplier-confirmation';
      this.scope.querySelectorAll('[data-availability]').forEach((label) => {
        label.hidden = state === 'unknown';
        if (state === 'unknown') {
          label.textContent = window.MinimogStrings?.unavailable || '';
          delete label.dataset.sdAvailabilityOverride;
          return;
        }
        if (isSupplierStock) {
          label.textContent = this.data.supplierStockLabel;
          label.dataset.sdAvailabilityOverride = 'true';
        } else if (isBackorder) {
          label.textContent = this.data.backorderLabel;
          label.dataset.sdAvailabilityOverride = 'true';
        } else {
          label.textContent = this.data.availableVariants[id] ? this.data.inStockLabel : this.data.outOfStockLabel;
          delete label.dataset.sdAvailabilityOverride;
        }
      });
      const stickySelect = this.scope.querySelector('sticky-atc [name="id"]');
      if (stickySelect) this.updateSticky(stickySelect.value);
    }

    updateSticky(variantId) {
      const state = this.data.variants[String(variantId)] || 'unknown';
      const message = this.data.messages[state];
      this.scope.querySelectorAll('[data-sd-sticky-fulfillment]').forEach((notice) => {
        notice.hidden = !message;
        if (message) notice.textContent = message.title;
      });
      this.scope.querySelector('sticky-atc')?.setStickyAddToCartHeight?.();
    }
  }
  customElements.define('sd-product-fulfillment', SpeedyDroneProductFulfillment);
}
